import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

interface Keyword {
  id: string;
  keyword: string;
  normalized: string;
  matchType: string;
  postcodeFrom: string | null;
  postcodeTo: string | null;
  weight: number;
  isNegative: boolean;
  source: string;
}

interface Preview {
  matched: boolean;
  normalizedAddress: string;
  zone: { id: string; code: string; name: string } | null;
  matchedKeyword?: string;
  matchType?: string;
  confidence?: number;
  lowConfidence?: boolean;
}

const MATCH_TYPES: Array<{ value: string; label: string; hint: string }> = [
  { value: 'CONTAINS', label: '包含', hint: '地址里出现这个词就算命中,最常用' },
  { value: 'PREFIX', label: '开头', hint: '地址必须以它开头' },
  { value: 'EXACT', label: '完全相同', hint: '整个地址就是这个词' },
  { value: 'POSTCODE', label: '邮编', hint: '邮编精确等于它' },
  { value: 'POSTCODE_RANGE', label: '邮编区间', hint: '邮编落在起止之间' },
];

/**
 * 区域关键词配置。
 *
 * ★ 这一屏最关键的不是增删,是**试匹配**。
 *
 *   配关键词最容易出的问题是「配完了不知道对不对」——
 *   规则之间有优先级(长词胜短词、排除词否决、邮编胜路名),
 *   光看列表推不出某个地址会落到哪个区。
 *   所以给一个输入框:贴一个真实地址,立刻看到结果和依据。
 */
export function ZoneKeywords({
  zoneId,
  zoneName,
  onClose,
}: {
  zoneId: string;
  zoneName: string;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<Keyword[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [keyword, setKeyword] = useState('');
  const [matchType, setMatchType] = useState('CONTAINS');
  const [pcFrom, setPcFrom] = useState('');
  const [pcTo, setPcTo] = useState('');
  const [isNegative, setIsNegative] = useState(false);

  const [testAddress, setTestAddress] = useState('');
  const [testPostcode, setTestPostcode] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  /**
   * ★ 加/删关键词之后要重跑试匹配。
   *
   * 一开始写的是 `setTestAddress((a) => a)` —— 设成同一个值,
   * React 认为状态没变,effect 根本不会重跑,预览还停在旧结果上。
   * 用户刚加完一条词,预览却仍显示「没有任何区域命中」——
   * 这一屏存在的意义就是消除这种困惑,结果它自己制造了一个。
   */
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    try {
      setRows(await api<Keyword[]>(`/zones/${zoneId}/keywords`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '加载失败');
    }
  }, [zoneId]);

  useEffect(() => {
    void load();
  }, [load]);

  // 试匹配防抖 —— 边打字边查会打出很多无用请求
  useEffect(() => {
    if (!testAddress.trim()) {
      setPreview(null);
      return;
    }
    const t = setTimeout(() => {
      void api<Preview>('/zones/match-preview', {
        method: 'POST',
        body: {
          address: testAddress.trim(),
          ...(testPostcode.trim() ? { postcode: testPostcode.trim() } : {}),
        },
      })
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 400);
    return () => clearTimeout(t);
  }, [testAddress, testPostcode, refreshKey]);

  const isPostcode = matchType === 'POSTCODE' || matchType === 'POSTCODE_RANGE';

  async function add() {
    if (!keyword.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/zones/${zoneId}/keywords`, {
        method: 'POST',
        body: {
          keyword: keyword.trim(),
          matchType,
          ...(matchType === 'POSTCODE_RANGE'
            ? { postcodeFrom: pcFrom.trim(), postcodeTo: pcTo.trim() }
            : {}),
          isNegative,
        },
      });
      setKeyword('');
      setPcFrom('');
      setPcTo('');
      setIsNegative(false);
      await load();
      // 加完立刻重跑一次试匹配 —— 让人看到这一条有没有起作用
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '添加失败');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    try {
      await api(`/zones/${zoneId}/keywords/${id}`, { method: 'DELETE' });
      await load();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '删除失败');
    }
  }

  const positive = (rows ?? []).filter((k) => !k.isNegative);
  const negative = (rows ?? []).filter((k) => k.isNegative);

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="区域关键词"
        className="w-full max-w-3xl max-h-[90dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            关键词 · {zoneName}
          </h3>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>
        <p className="mb-4 text-sm text-slate-500">
          收货地址命中关键词就自动判到这个区。判不出区的单会进复核队列。
        </p>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* ── 试匹配:这一屏最重要的部分 ── */}
        <section className="mb-5 rounded-xl border border-brand-200 dark:border-brand-900
                            bg-brand-50/50 dark:bg-brand-500/5 p-4">
          <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            试一个地址
          </h4>
          <div className="flex gap-2">
            <input
              aria-label="试匹配地址"
              value={testAddress}
              onChange={(e) => setTestAddress(e.target.value)}
              placeholder="贴一个真实收货地址,立刻看到会判到哪个区"
              className={`${inputCls} flex-1`}
            />
            <input
              aria-label="试匹配邮编"
              value={testPostcode}
              onChange={(e) => setTestPostcode(e.target.value)}
              placeholder="邮编"
              className={`${inputCls} w-28`}
            />
          </div>

          {preview && (
            <div className="mt-2 text-sm">
              {preview.matched ? (
                <>
                  <span className="text-slate-600 dark:text-slate-400">判到:</span>{' '}
                  <span
                    className={`font-medium ${
                      preview.zone?.id === zoneId
                        ? 'text-green-700 dark:text-green-400'
                        : 'text-amber-700 dark:text-amber-400'
                    }`}
                  >
                    {preview.zone!.code} {preview.zone!.name}
                    {preview.zone?.id !== zoneId && '(不是当前区域)'}
                  </span>
                  {/* 凭哪条规则命中要说清楚 —— 判错时才知道从哪改 */}
                  <div className="mt-0.5 text-xs text-slate-500">
                    命中「{preview.matchedKeyword}」· {preview.matchType} · 强度{' '}
                    {preview.confidence}
                    {preview.lowConfidence && (
                      <span className="ml-2 text-amber-600 dark:text-amber-400">
                        ⚠ 强度偏低,这类单会进复核队列
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <span className="text-amber-700 dark:text-amber-400">
                  没有任何区域命中 —— 这类地址的单会进复核队列
                </span>
              )}
              <div className="mt-1 text-xs text-slate-400 break-all">
                规整化后:{preview.normalizedAddress}
              </div>
            </div>
          )}
        </section>

        {/* ── 添加 ── */}
        <section className="mb-5">
          <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            添加关键词
          </h4>
          <div className="flex gap-2 flex-wrap">
            <input
              aria-label="关键词"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={isPostcode ? '给这条规则起个名字' : '例如 Great North Road'}
              className={`${inputCls} flex-1 min-w-48`}
            />
            <select
              aria-label="匹配方式"
              value={matchType}
              onChange={(e) => setMatchType(e.target.value)}
              className={`${inputCls} w-32`}
            >
              {MATCH_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {matchType === 'POSTCODE_RANGE' && (
            <div className="mt-2 flex gap-2 items-center">
              <input
                aria-label="邮编起"
                value={pcFrom}
                onChange={(e) => setPcFrom(e.target.value)}
                placeholder="50000"
                className={`${inputCls} w-28`}
              />
              <span className="text-slate-400">到</span>
              <input
                aria-label="邮编止"
                value={pcTo}
                onChange={(e) => setPcTo(e.target.value)}
                placeholder="50999"
                className={`${inputCls} w-28`}
              />
            </div>
          )}

          <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={isNegative}
                onChange={(e) => setIsNegative(e.target.checked)}
              />
              设为排除词
            </label>
            <button
              onClick={() => void add()}
              disabled={busy || !keyword.trim()}
              className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium
                         disabled:opacity-40"
            >
              {busy ? '添加中…' : '添加'}
            </button>
          </div>

          <p className="mt-1.5 text-xs text-slate-400">
            {isNegative
              ? // ★ 排除词的语义不直观,必须解释
                '排除词是否决权:地址命中它,这个区域就直接出局 —— 用来从一个大范围里挖掉一小块'
              : MATCH_TYPES.find((t) => t.value === matchType)?.hint}
          </p>
        </section>

        {/* ── 已配置 ── */}
        <section>
          <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            已配置({rows?.length ?? 0})
          </h4>

          {rows?.length === 0 && (
            <p className="text-sm text-slate-400">
              还没有关键词 —— 这个区域不会被自动判到,只能建单时手工指定
            </p>
          )}

          {positive.length > 0 && <KeywordList items={positive} onRemove={remove} />}

          {negative.length > 0 && (
            <>
              <div className="mt-3 mb-1 text-xs text-slate-500">排除词(命中则本区出局)</div>
              <KeywordList items={negative} onRemove={remove} />
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function KeywordList({
  items,
  onRemove,
}: {
  items: Keyword[];
  onRemove: (id: string) => void | Promise<void>;
}) {
  return (
    <ul className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y
                   divide-slate-100 dark:divide-slate-700/50">
      {items.map((k) => (
        <li key={k.id} className="px-3 py-2 flex items-center gap-3 text-sm">
          <span
            className={`shrink-0 px-1.5 py-0.5 rounded text-xs ${
              k.isNegative
                ? 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
            }`}
          >
            {MATCH_TYPES.find((t) => t.value === k.matchType)?.label ?? k.matchType}
          </span>
          <span className="min-w-0 flex-1 truncate text-slate-900 dark:text-slate-100">
            {k.keyword}
            {k.matchType === 'POSTCODE_RANGE' && k.postcodeFrom && (
              <span className="text-slate-400">
                {' '}
                {k.postcodeFrom}–{k.postcodeTo}
              </span>
            )}
          </span>
          {/* 规整化结果要显示 —— 匹配用的是它,不显示的话
              「为什么这个词没命中」永远说不清 */}
          <span className="shrink-0 text-xs text-slate-400 font-mono max-w-40 truncate">
            {k.normalized}
          </span>
          <button
            onClick={() => void onRemove(k.id)}
            aria-label={`删除关键词 ${k.keyword}`}
            className="shrink-0 text-xs text-red-600 dark:text-red-400"
          >
            删除
          </button>
        </li>
      ))}
    </ul>
  );
}

const inputCls =
  'h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm';
